import * as Sentry from "@sentry/react";
import { Button, toast } from "@ndd/ui";
import { Copy } from "lucide-react";
import { useState, type ReactElement } from "react";
import {
  appointmentDraftCopyFeedback,
  beginAppointmentDraftClipboardWrite,
  type AppointmentDraftClipboardWrite,
  type AppointmentDraftCopyOutcome,
} from "@/lib/appointment-draft-copy";

export function CopyDraftButton({
  disabled = false,
  copyCurrent,
}: {
  disabled?: boolean;
  copyCurrent: (
    clipboard: AppointmentDraftClipboardWrite
  ) => Promise<AppointmentDraftCopyOutcome>;
}): ReactElement {
  const [checking, setChecking] = useState(false);

  async function handleCopy(): Promise<void> {
    setChecking(true);

    try {
      const clipboard = beginAppointmentDraftClipboardWrite();
      const feedback = appointmentDraftCopyFeedback(await copyCurrent(clipboard));
      if (feedback.kind === "success") toast.success(feedback.message);
      else toast.error(feedback.message);
    } catch {
      Sentry.captureException(new Error("appointment_draft_copy_failed"), {
        tags: {
          event: "appointment_drafts.copy_failed",
          operation: "copy",
          source: "client",
          surface: "clipboard",
        },
      });
      toast.error("Couldn't copy the note. Try again.");
    } finally {
      setChecking(false);
    }
  }

  return (
    <Button
      type="button"
      variant="utility"
      size="compact"
      disabled={disabled || checking}
      onClick={() => void handleCopy()}
    >
      <Copy aria-hidden="true" />
      {checking ? "Checking…" : "Copy note"}
    </Button>
  );
}
